import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Tooltip,
  LinearProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Replay as RetryIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Sync as SyncIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { syncLogApi } from '../../../services/sisApi';
import { SISSyncLog } from '../../../types/sis';

const SyncStatusIcon: React.FC<{ status: string }> = ({ status }) => {
  const icons = {
    completed: <CheckIcon color="success" />,
    failed: <ErrorIcon color="error" />,
    in_progress: <SyncIcon color="warning" />,
    partial: <WarningIcon color="warning" />,
    started: <ScheduleIcon color="info" />,
  };
  return icons[status as keyof typeof icons] || <SyncIcon />;
};

const SyncLogs: React.FC = () => {
  const [filters, setFilters] = useState({
    sync_type: '',
    status: '',
    entity_type: '',
    page: 1,
  });
  const [selectedLog, setSelectedLog] = useState<SISSyncLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const queryClient = useQueryClient();

  // Fetch sync logs
  const { data: logsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['syncLogs', filters],
    queryFn: () => syncLogApi.getAll({
      sync_type: filters.sync_type || undefined,
      status: filters.status || undefined,
      entity_type: filters.entity_type || undefined,
      page: filters.page,
      page_size: 20,
    }),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Retry sync mutation
  const retrySyncMutation = useMutation({
    mutationFn: (id: number) => syncLogApi.retry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['syncLogs'] });
    },
  });

  const logs = logsResponse?.data?.results || [];
  const totalPages = Math.ceil((logsResponse?.data?.count || 0) / 20);

  const syncTypes = [
    { value: '', label: 'All Types' },
    { value: 'full', label: 'Full Sync' },
    { value: 'incremental', label: 'Incremental' },
    { value: 'real_time', label: 'Real Time' },
    { value: 'webhook', label: 'Webhook' },
  ];

  const statuses = [
    { value: '', label: 'All Statuses' },
    { value: 'started', label: 'Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'partial', label: 'Partial' },
  ];

  const entityTypes = [
    { value: '', label: 'All Entities' },
    { value: 'school', label: 'Schools' },
    { value: 'student', label: 'Students' },
    { value: 'staff', label: 'Staff' },
    { value: 'all', label: 'All Data' },
  ];

  const handleViewDetails = (log: SISSyncLog) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const handleRetry = (id: number) => {
    if (window.confirm('Retry this sync operation?')) {
      retrySyncMutation.mutate(id);
    }
  };

  const formatDuration = (duration?: string) => {
    if (!duration) return '-';
    
    // Parse duration string (e.g., "0:01:23.456789")
    const parts = duration.split(':');
    if (parts.length === 3) {
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      const seconds = parseFloat(parts[2]);
      
      if (hours > 0) {
        return `${hours}h ${minutes}m ${Math.round(seconds)}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${Math.round(seconds)}s`;
      } else {
        return `${seconds.toFixed(1)}s`;
      }
    }
    return duration;
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'in_progress':
      case 'partial':
        return 'warning';
      case 'started':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSyncTypeColor = (type: string): 'primary' | 'secondary' | 'success' | 'warning' => {
    switch (type) {
      case 'full':
        return 'primary';
      case 'real_time':
        return 'success';
      case 'webhook':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Synchronization Logs</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Sync Type"
          value={filters.sync_type}
          onChange={(e) => setFilters({ ...filters, sync_type: e.target.value, page: 1 })}
          sx={{ minWidth: 150 }}
          size="small"
        >
          {syncTypes.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
          sx={{ minWidth: 150 }}
          size="small"
        >
          {statuses.map((status) => (
            <MenuItem key={status.value} value={status.value}>
              {status.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Entity Type"
          value={filters.entity_type}
          onChange={(e) => setFilters({ ...filters, entity_type: e.target.value, page: 1 })}
          sx={{ minWidth: 150 }}
          size="small"
        >
          {entityTypes.map((entity) => (
            <MenuItem key={entity.value} value={entity.value}>
              {entity.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Auto-refresh indicator */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Logs are automatically refreshed every 30 seconds. Live sync operations will appear here in real-time.
        </Typography>
      </Alert>

      {/* Loading */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load sync logs: {(error as any)?.message || 'Unknown error'}
        </Alert>
      )}

      {/* Logs Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sync Activities
          </Typography>

          {logs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No sync logs found
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Records</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log: any) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Chip
                          label={log.sync_type}
                          color={getSyncTypeColor(log.sync_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{log.entity_type}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SyncStatusIcon status={log.status} />
                          <Chip
                            label={log.status}
                            color={getStatusColor(log.status)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        {log.records_processed > 0 ? (
                          <Box>
                            <Typography variant="body2">
                              {log.records_processed} processed
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.records_created} created, {log.records_updated} updated
                              {log.records_failed > 0 && (
                                <span style={{ color: 'red' }}>
                                  , {log.records_failed} failed
                                </span>
                              )}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.started_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDuration(log.execution_time)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.error_message ? (
                          <Tooltip title={log.error_message}>
                            <Chip
                              label="Has Error"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(log)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {log.status === 'failed' && (
                          <Tooltip title="Retry Sync">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleRetry(log.id!)}
                              disabled={retrySyncMutation.isPending}
                            >
                              <RetryIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={filters.page}
            onChange={(_, page) => setFilters({ ...filters, page })}
            color="primary"
          />
        </Box>
      )}

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Sync Log Details
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Sync Type
                </Typography>
                <Chip 
                  label={selectedLog.sync_type} 
                  color={getSyncTypeColor(selectedLog.sync_type)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SyncStatusIcon status={selectedLog.status} />
                  <Chip 
                    label={selectedLog.status} 
                    color={getStatusColor(selectedLog.status)}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Entity Type
                </Typography>
                <Typography variant="body2">
                  {selectedLog.entity_type}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Execution Time
                </Typography>
                <Typography variant="body2">
                  {formatDuration(selectedLog.execution_time)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Started At
                </Typography>
                <Typography variant="body2">
                  {new Date(selectedLog.started_at).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Completed At
                </Typography>
                <Typography variant="body2">
                  {selectedLog.completed_at 
                    ? new Date(selectedLog.completed_at).toLocaleString()
                    : 'Not completed'
                  }
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Record Statistics
                </Typography>
                <Grid container spacing={1}>
                  <Grid item>
                    <Chip 
                      label={`${selectedLog.records_processed} Processed`} 
                      variant="outlined" 
                      size="small" 
                    />
                  </Grid>
                  <Grid item>
                    <Chip 
                      label={`${selectedLog.records_created} Created`} 
                      color="success" 
                      variant="outlined" 
                      size="small" 
                    />
                  </Grid>
                  <Grid item>
                    <Chip 
                      label={`${selectedLog.records_updated} Updated`} 
                      color="info" 
                      variant="outlined" 
                      size="small" 
                    />
                  </Grid>
                  {selectedLog.records_failed > 0 && (
                    <Grid item>
                      <Chip 
                        label={`${selectedLog.records_failed} Failed`} 
                        color="error" 
                        variant="outlined" 
                        size="small" 
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {selectedLog.error_message && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Error Message
                  </Typography>
                  <Alert severity="error">
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedLog.error_message}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          {selectedLog?.status === 'failed' && (
            <Button
              onClick={() => {
                handleRetry(selectedLog.id!);
                setDetailsOpen(false);
              }}
              variant="contained"
              color="warning"
              startIcon={<RetryIcon />}
              disabled={retrySyncMutation.isPending}
            >
              Retry Sync
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SyncLogs;